/**
 * OMNICHANNEL-TIMELINE-AND-LEAD-360-V1 -- the Lead 360 composer's ADD A NOTE
 * action (C1). LOG A CALL is NOT here: it stays the existing
 * LeadDispositionPanel + POST /api/leads/:id/disposition (one vocabulary,
 * one writer).
 *
 * WRITE LAW (C3): idempotent submits -- ONE opaque key is minted per
 * composed note (lazily, at first submit) and re-sent on every retry of
 * that same note, so a double-tap or a timeout-retry cannot double-log;
 * the key regenerates ONLY after a confirmed save. The save button
 * disables while in flight; failures render INLINE beside the field
 * (S-8; server 400 messages verbatim -- LES-009 error passthrough), and
 * the saved state is visible where the content was authored (LES-012).
 * No toast (S-7).
 *
 * HONEST SEAM: when the notes store is not yet applied (migration
 * 20260804_4 pending founder apply), the host passes
 * notesStoreAvailable=false and this composer renders the seam line
 * INSTEAD of a dead form (LES-016: no door that cannot open).
 *
 * PHI: bounded, digit-run-guarded server-side; the hint states the rule so
 * the operator learns it before the refusal.
 */

import { useRef, useState, type FormEvent } from 'react';

import { PrimaryAction } from '@/dashboard/ui';

export interface LeadNoteSavedActivity {
  readonly id: number;
  readonly occurredAt: string;
  readonly duplicateOfRetry: boolean;
}

export interface LeadNoteComposerProps {
  /** POST the note (the typed api.ts fetcher, bound to the lead). */
  readonly save: (body: {
    body: string;
    idempotencyKey: string;
  }) => Promise<{ activity: { id: number; occurredAt: string; duplicateOfRetry: boolean } }>;
  /** Called after a confirmed save so the host refreshes the timeline IN
   *  PLACE (C3: the new touch appears at the top without a page reload). */
  readonly onSaved: (saved: LeadNoteSavedActivity) => void;
  /** FALSE = the 20260804_4 store is not applied yet -> the seam renders. */
  readonly notesStoreAvailable: boolean;
}

/** Mint an opaque idempotency key (browser crypto when present; a
 *  time+random fallback otherwise -- both satisfy the server pattern). */
export function mintIdempotencyKey(): string {
  const cryptoObj = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoObj !== undefined && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID().replace(/-/g, '');
  }
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export const NOTE_SEAM_COPY =
  'Notes are not enabled on this environment yet (pending the founder-applied lead_activities migration).';

export default function LeadNoteComposer({
  save,
  onSaved,
  notesStoreAvailable,
}: LeadNoteComposerProps): JSX.Element {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedLine, setSavedLine] = useState<string | null>(null);
  // The per-composed-note idempotency key: minted at first submit, HELD
  // across retries of the same text, cleared only on confirmed save.
  const keyRef = useRef<string | null>(null);

  if (!notesStoreAvailable) {
    return (
      <div className="lead-note-composer" data-testid="lead-note-composer-seam">
        <p className="lead360-muted">{NOTE_SEAM_COPY}</p>
      </div>
    );
  }

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (saving) return;
    const body = text.trim();
    if (body === '') {
      setError('Write the note first.');
      return;
    }
    if (keyRef.current === null) {
      keyRef.current = mintIdempotencyKey();
    }
    setSaving(true);
    setError(null);
    save({ body, idempotencyKey: keyRef.current })
      .then((res) => {
        keyRef.current = null;
        setText('');
        setSavedLine(
          res.activity.duplicateOfRetry
            ? 'Note already saved (retry detected - not duplicated).'
            : 'Note saved.',
        );
        onSaved({
          id: res.activity.id,
          occurredAt: res.activity.occurredAt,
          duplicateOfRetry: res.activity.duplicateOfRetry,
        });
      })
      .catch((err: unknown) => {
        // LES-009 error passthrough: the server's named validation message
        // renders verbatim; raw internals stay generic.
        setError(err instanceof Error ? err.message : 'The note could not be saved.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <form
      className="lead-note-composer"
      data-testid="lead-note-composer"
      onSubmit={submit}
      aria-label="Add a note"
    >
      <label className="lead-note-composer__label" htmlFor="lead-note-composer-text">
        Add a note
      </label>
      <textarea
        id="lead-note-composer-text"
        className="lead-note-composer__text"
        value={text}
        maxLength={2000}
        rows={3}
        placeholder="Commercial context only - no phone numbers (use the audited reveal), no clinical content."
        onChange={(ev): void => {
          setText(ev.target.value);
          if (savedLine !== null) setSavedLine(null);
        }}
        data-testid="lead-note-composer-text"
      />
      <div className="lead-note-composer__foot">
        {error !== null ? (
          <p className="lead-note-composer__error" data-testid="lead-note-composer-error">
            {error}
          </p>
        ) : savedLine !== null ? (
          <p className="lead-note-composer__saved" data-testid="lead-note-composer-saved">
            {savedLine}
          </p>
        ) : (
          <span className="lead-note-composer__hint lead360-muted">
            Appears on the timeline instantly. Notes are append-only (a
            correction is a new note).
          </span>
        )}
        <PrimaryAction
          type="submit"
          size="sm"
          disabled={saving}
          data-testid="lead-note-composer-save"
        >
          {saving ? 'Saving...' : 'Save note'}
        </PrimaryAction>
      </div>
    </form>
  );
}
